<template>
  <div>
    <popup-hint hint="bulkcontainers">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Bulk containers"/>
      </template>
      <template v-slot:top>
        Bulk containers are transient vessels (unlike tanks, which are permanent vessels) of spirits or wine like pails, drums, or totes.
        They are commonly used to bring in bulk alcohol or ship out bulk alcohol.
      </template>
      <template v-slot:bottom>
        You can create bulk containers individually or do a batch import of bulk containers.
      </template>
      <template v-slot:actions="{close}">
        <v-btn color="primary" class="ml-4" small @click="close();$refs.import.doImport()">Import</v-btn>
        <v-btn @click="close()" :to="{name:'InventoryBulkContainersEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <import-csv v-model="doImport" type="bulk containers" :fields="importFields" importAction="bulkContainersImport" :instructions="instructions" ref="import" @refresh="refresh"/>

    <purtrak-table name="bulkcontainers" objectType="bulk containers" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select
                   listEndPoint="bulkContainers2" v-model="selected"
                   fields="id,number,location,type{name}
                           contents{volume,litres,strength,kg,value,alcoholName,alcoholType,lot,userLicense,siteName,siteId,imported}"
                   totalFields="totalVolume,totalValue,totalLitres"
                   :totals.sync="totals"
                   :listFilters="listFilters" :parameters="listParameters"
                   @dblclick="bulkContainer => $router.push({name:'InventoryBulkContainersTransactions',params:{bulkContainerId:bulkContainer.id}})"
                   title="Bulk Containers"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[`As of ${$formatDate(filters.asof)}`,
                                            filters.siteId!=null?siteName:'',filters.contents!='0'?filters.contents=='1'?'Spirits':(filters.contents==='2'?'Wine':'Non-excise'):'',
                                            filters.license!='2'?(filters.license=='0'?'Spirits license':'User license'):'']}"
                   download-endpoint="bulkContainersExportCSV">
      <template v-slot:filter>
        <scan-code @code="scan" return-unmatched-code auto-close/>
        <filter-menu include-reset @reset="filters.empty='0';filters.siteId=null;filters.license='2';filters.contents='0';filters.asof=today;filters.search='';filters.id=null">
          <v-list-item>
            <filter-search :label="[{text:'Number',value:'number'},{text:'Contents',value:'contents.alcoholName'},{text:'Lot',value:'contents.lot'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <v-checkbox v-model="filters.empty" true-value="1" false-value="0" label="Include empty containers" dense class="mt-n2"/>
          </v-list-item>
          <v-list-item v-if="sites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Contents" dense outlined :items="[{value:'0',text:'Any'},{divider:true},{value:'1',text:'Spirits'},{value:'2',text:'Wine'},{value:'3',text:'Non-excise'}]" v-model="filters.contents"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="License" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'User'},{value:'0',text:'Spirits'}]" v-model="filters.license"/>
          </v-list-item>
          <v-list-item>
            <date-picker label="As of" v-model="filters.asof" date-only/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.id" close small color="primary" class="ml-1" @click:close="filters.id=null">Scanned bulk container</v-chip>
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.empty=='1'" close small class="ml-1" @click:close="filters.empty='0'">Include empty</v-chip>
          <v-chip v-show="filters.asof!==today" close small class="ml-1" @click:close="filters.asof=today">As of {{filters.asof | formatDate}}</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null">{{siteName}}</v-chip>
          <v-chip v-show="filters.contents!='0'" close small class="ml-1" @click:close="filters.contents='0'">{{filters.contents=='1'?'Spirits':(filters.contents==='2'?'Wine':'Non-excise')}}</v-chip>
          <v-chip v-show="filters.license!='2'" close small class="ml-1" @click:close="filters.license='2'">{{filters.license=='0'?'Spirits license':'User license'}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'InventoryBulkContainersEditNew'}"><v-list-item-title>Create bulk container</v-list-item-title></v-list-item>
          <v-list-item v-if="!$vuetify.breakpoint.mobile" @click="doImport=true"><v-list-item-title>Import bulk containers from CSV</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-list-item :to="{name:'InventoryBulkContainersTypes'}"><v-list-item-title>Bulk container types</v-list-item-title></v-list-item>
        <template v-if="$store.getters.company.connectedToAccounting">
          <v-divider/>
          <v-list-item @click="sync"><v-list-item-title>Sync bulk containers with QBO</v-list-item-title></v-list-item>
        </template>
        <v-divider/>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Bulk Containers',to:{name:'InventoryBulkContainers'}}]},query:{to:'reports/inventory/ReportsInventoryBulkContainersPhysical'}}"><v-list-item-title>Physical inventory worksheet</v-list-item-title></v-list-item>
        <v-list-item :to="{name:'ExciseVesselReconcile',params:{type:'BulkContainer'}}"><v-list-item-title>Reconcile bulk container inventory</v-list-item-title></v-list-item>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <inventory-bulk-containers-batch-edit :bulkContainers="selected" @refresh="$refs.table.buildList($event)"/>
      </template>
      <template v-slot:item.number="{item,printing}">
        <template v-if="printing">{{item.number}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'InventoryBulkContainersEdit',params:{bulkContainerId:item.id}})"
               :class="{'text-decoration-underline':hover}">{{item.number}}</a>
          </v-hover>
          <template v-else>{{item.number}}</template>
          <div v-if="item.contents.userLicense" class="grey--text text-caption">User License</div>
        </template>
      </template>
      <template v-slot:item.contents.alcoholName="{item,printing}">
        <template v-if="item.contents.volume!==0">
          <template v-if="printing">{{item.contents.alcoholName}}</template>
          <div v-else :class="{'green--text':item.contents.proofed}">
            {{item.contents.alcoholName}}
            <template v-if="item.contents.alcoholType!=='Spirit'">
              <div v-if="item.contents.alcoholType==='NonExcise'" class="text-caption grey--text">Non-excise</div>
              <div v-else class="text-caption">Wine</div>
            </template>
            <div v-else-if="item.contents.imported" class="text-caption grey--text">Imported</div>
          </div>
        </template>
        <template v-else>&nbsp;</template>
      </template>
      <template v-slot:item.contents.volume="{item}">
        <template v-if="item.contents.alcoholType === 'Spirit' || !item.contents.volume">
          {{item.contents.volume | number(3)}} LAA
        </template>
        <template v-else>
          {{item.contents.volume | number(3)}} litres
        </template>
      </template>
      <template v-slot:item.contents.litres="{value}">
        {{value | number(3)}}
      </template>
      <template v-slot:item.contents.strength="{value}">
        {{value | number(2)}}%
      </template>
      <template v-slot:item.contents.kg="{value}">
        {{value | number(3)}} kg
      </template>
      <template v-slot:item.contents.value="{value}">
        {{value | currency}}
      </template>
      <template v-slot:item.contents.lot="{item,value,printing}">
        <v-hover v-if="item.contents.lot && !printing" v-slot="{hover}">
          <a @click.stop="$router.push({name:'ReportsView',params:{back:[{text:'Bulk containers',to:{name:'InventoryBulkContainers'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:item.contents.lot,forward:1}})"
             :class="{'text-decoration-underline':hover}">{{item.contents.lot}}</a>
        </v-hover>
        <template v-else>{{value}}</template>
      </template>
      <template v-slot:item.contents.userLicense="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.contents.imported="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'InventoryBulkContainersEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <inventory-bulk-container-menu :bulkContainer="item" :readOnly="readOnly" @delete="bulkContainerToDelete=item;showDelete=true"/>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='contents.volume'">
              {{totals.totalVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='contents.litres'">
              {{totals.totalLitres | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='contents.value' && values">
              {{totals.totalValue | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="'Bulk container ' + bulkContainerToDelete.number" @delete="performDeleteBulkContainer"/>
  </div>
</template>

<script>
import InventoryBulkContainerMenu from '@/views/inventory/bulkContainers/InventoryBulkContainerMenu'
import InventoryBulkContainersBatchEdit from '@/views/inventory/bulkContainers/InventoryBulkContainersBatchEdit'
import {format} from 'date-fns'

export default {
  name: 'InventoryBulkContainers',
  components: {InventoryBulkContainerMenu,InventoryBulkContainersBatchEdit},
  data() {
    return {
      filters: {
        empty: '0',
        asof: format(new Date(), 'yyyy-MM-dd'),
        siteId: null,
        search: '',
        searchField: 'number',
        contents: '0',
        license: '2',
        id: null
      },
      searchDisplay: '',
      totals: {totalValue:0, totalLitres:0, totalVolume:0},
      today: format(new Date(), 'yyyy-MM-dd'),
      bulkContainerToDelete: {},
      showDelete: false,
      doImport: false,
      importFields: [{name:'Number',mapTo:'',mandatory:true},{name:'Alcohol type',mapTo:'',mandatory:true},
                     {name:'Alcohol name',mapTo:'',mandatory:true},{name:'Volume',mapTo:'',mandatory:true},{name:'Litres',mapTo:'',mandatory:false},
                     {name:'Value',mapTo:'',mandatory:false},{name:'Lot',mapTo:'',mandatory:false},
                     {name:'Date',mapTo:'',mandatory:false,date:true}],
      instructions: `<ol class="mb-4">
                        <li>Number</li>
                        <li>Alcohol type ("spirit", "wine" or "non-excise")</li>
                        <li>Alcohol name</li>
                        <li>Volume (LAA)</li>
                        <li>Litres (optional, default is 0)</li>
                        <li>Value (optional, default is $0)</li>
                        <li>Lot (optional, default is L0)</li>
                        <li>Date (optional, default is today)</li>
                      </ol>
                      <em>Note that not all information for a bulk container can be imported.</em>
                      You should, therefore, check each imported bulk container to ensure all settings are correct.`,
      defaultColumns: [{ value: 'number', visible:true},
                       { value: 'contents.siteName', visible:true},
                       { value: 'contents.alcoholName', visible:true},
                       { value: 'contents.lot', visible:true},
                       { value: 'contents.volume', visible:true},
                       { value: 'contents.litres', visible:false},
                       { value: 'contents.strength', visible:false},
                       { value: 'contents.kg', visible:false},
                       { value: 'contents.value', visible:true},
                       { value: 'location', visible:false},
                       { value: 'contents.userLicense', visible:false},
                       { value: 'contents.imported', visible:false}
      ],
      headers: [{ text: 'CONTAINER #', value: 'number', width:'15%',sortable:true},
                { text: 'SITE', value: 'contents.siteName', width:'20%',sortable:true, omit:() => this.sites.length <= 1},
                { text: 'CONTENTS', value: 'contents.alcoholName', width:'25%',sortable:true},
                { text: 'LOT', value: 'contents.lot', width:'10%',sortable:true},
                { text: 'LOCATION', value: 'location', width:'15%',sortable:true},
                { text: 'VOLUME', value: 'contents.volume', width:'12%',sortable:true,align:'right'},
                { text: 'LITRES', value: 'contents.litres', width:'12%',sortable:true,align:'right', omit:() => !this.trackLitres},
                { text: 'STRENGTH', value: 'contents.strength', width:'12%',sortable:true,align:'right', omit:() => !this.trackStrength},
                { text: 'WEIGHT', value: 'contents.kg', width:'12%',sortable:true,align:'right', omit:() => !this.trackKg},
                { text: 'VALUE', value: 'contents.value', width:'12%',sortable:true,align:'right', omit:() => !this.values},
                { text: 'USER LIC', value: 'contents.userLicense', width:'10%',sortable:true,align:'center'},
                { text: 'IMPORTED', value: 'contents.imported', width:'10%',sortable:true,align:'center'}
                ]
    }
  },
  computed: {
    sites() {
      return this.$store.getters.sites(true, {B263:true,B265:true,B266:true});
    },
    siteName() {
      return this.sites.find(site => site.id === this.filters.siteId)?.name;
    },
    readOnly() {
      return !this.$store.getters.company.permissions.includes('inventory.write');
    },
    values() {
      return this.$store.getters.company.permissions.includes('values');
    },
    listFilters() {
      const filters = [];
      if (this.filters.id)
        filters.push({field:'id', comparator:'eq', value:this.filters.id});
      if (this.filters.contents == '1')
        filters.push({field:'alcoholType', comparator:'eq', value:'Spirit'});
      else if (this.filters.contents == '2')
        filters.push({field:'alcoholType', comparator:'ne', value:'Spirit'},{field:'alcoholType', comparator:'ne', value:'NonExcise'});
      else if (this.filters.contents == '3')
        filters.push({field:'alcoholType', comparator:'eq', value:'NonExcise'});

      if (this.filters.license == '1')
        filters.push({field:'userLicense', comparator:'eq', value:'true'});
      else if (this.filters.license == '0')
        filters.push({field:'userLicense', comparator:'eq', value:'false'});

      if (this.filters.siteId)
        filters.push({field:'siteId', comparator:'eq', value:this.filters.siteId});

      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:this.filters.searchField === 'number' ? 'eq' : 'contains', value:this.filters.search});

      if (this.filters.empty=='0')
        filters.push({field:'contents.volume', comparator:'ne', value:'0'});

      return filters;
    },
    listParameters() {
      return this.filters.asof ? [{name:'asof', value:`${this.filters.asof} 23:59:59`}] : [];
    },
    trackLitres() {
      return this.$store.getters.company.settings.trackLitres;
    },
    trackKg() {
      return this.$store.getters.company.settings.trackKg;
    },
    trackStrength() {
      return this.$store.getters.company.settings.trackStrength;
    },
  },
  async created() {
    this.$store.dispatch('checkTasks');
  },
  methods: {
    scan({type, id}) {
      if (type === 'BulkContainer')
        Object.assign(this.filters, {empty:'1',siteId:null,license:'2',contents:'0',asof:this.today,search:'',id});
    },
    async refresh() {
      this.$refs.table.buildList();
    },
    async performDeleteBulkContainer() {
      this.loading = true;
      const results = await this.$apiCall(`mutation InventoryBulkContainers($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.bulkContainerToDelete.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$refs.table.buildList();
        this.$store.commit('setError', {type:'success',message:`Deleted Bulk container ${this.bulkContainerToDelete.number}`});
      }
    },
    async sync() {
      this.loading = true;
      const results = await this.$apiCall(`mutation InventoryBulkContainers {
                                             accountingIntegrationSync(step:"bulkContainers") {name}
                                           }`, {});
      await this.refresh();
      if (!results.error)
        this.$store.commit('setError', {type:'success',message:`Synced bulk containers with QBO`});
    }
  }
}
</script>